import { Component } from '@angular/core';
import { Location } from '@angular/common'
import { ScenarioAssessmentSavePayload, ScenarioAssessmentSectionDto, ScenarioAssessmentService, UserProfileResponse } from '../scenario-assessment.service';

type DomainKey = 'A' | 'B' | 'C' | 'D';

interface ScenarioOption {
  id: number;
  text: string;
  sequence: string;
}

interface ScenarioQuestion {
  no: number; // 1..n
  domainKey: DomainKey;
  domainTitle: string;
  examId: number;
  sectionId: number;
  questionId: number;
  title: string;
  situation: string;
  prompt: string;
  options: ScenarioOption[];
}

@Component({
  selector: 'app-scenario-assessment',
  templateUrl: './scenario-assessment.component.html',
  styleUrls: ['./scenario-assessment.component.scss']
})
export class ScenarioAssessmentComponent {
  loading = true;
  errorMessage = '';
  submitting = false;
  /** Prevent multiple submit API calls (manual + auto + visibilitychange) */
  private submitInFlight = false;
  /** Backend says already submitted; hide user/questions content */
  alreadySubmitted = false;
  alreadySubmittedMessage = '';

  started = false;
  /** True after successful submit — shows confirmation screen. */
  submitted = false;
  showSubmitConfirm = false;
  completed = false;
  /** After successful save: true if submit ran when the countdown had reached 0 (timer / deadline). */
  completedDueToTimeUp = false;

  readonly totalTimeSeconds = 20 * 60;
  remainingSeconds = this.totalTimeSeconds;
  private timerId: ReturnType<typeof setInterval> | null = null;
  private endAtMs: number | null = null;

  currentIndex = 0;
  selectedOptionByQuestionId = new Map<number, number>();

  questions: ScenarioQuestion[] = [];
  private sectionsRaw: ScenarioAssessmentSectionDto[] = [];

  private attemptStartAtMs: number | null = null;
  private questionShownAtMs: number | null = null;
  private timeTakenByQuestionId = new Map<number, number>();

  staffName = '—';
  /** Profile card: payroll id (same order as self-assessment). */
  staffPayrollId = '—';
  staffDesignation = '—';
  staffCampus = '—';

  /** Profile photo from API (blob URL). */
  profileImageObjectUrl: string | null = null;
  private profileImageRevokeUrl: string | null = null;

  constructor(
    private readonly location: Location,
    private readonly api: ScenarioAssessmentService
  ) {}

  ngOnInit(): void {
    this.loadProfile();
    this.loadQuestions();
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.revokeProfileImageObjectUrl();
  }

  confirmationBack(): void {
    this.location.back();
  }

  begin(): void {
    if (this.loading || this.errorMessage || this.questions.length === 0) return;
    this.submitted = false;
    this.started = true;
    this.completed = false;
    this.completedDueToTimeUp = false;
    this.currentIndex = 0;
    this.selectedOptionByQuestionId.clear();
    this.timeTakenByQuestionId.clear();
    this.remainingSeconds = this.totalTimeSeconds;
    this.endAtMs = Date.now() + this.totalTimeSeconds * 1000;
    this.attemptStartAtMs = Date.now();
    this.questionShownAtMs = Date.now();
    this.submitting = false;
    this.submitInFlight = false;
    this.startTimer();
  }

  back(): void {
    if (!this.started) {
      this.location.back();
      return;
    }
    this.flushTimeForCurrentQuestion();
    if (this.currentIndex > 0) this.currentIndex -= 1;
    this.questionShownAtMs = Date.now();
  }

  next(): void {
    if (!this.canGoNext) return;
    this.flushTimeForCurrentQuestion();
    if (this.currentIndex < this.questions.length - 1) this.currentIndex += 1;
    this.questionShownAtMs = Date.now();
  }

  /** @param autoTimeUp true when the timer fired — auto-save with no confirm dialog. */
  submit(autoTimeUp = false): void {
    if (this.completed || this.submitting || this.submitInFlight) return;

    const isTimeUp =
      autoTimeUp ||
      this.remainingSeconds <= 0 ||
      (this.endAtMs != null && Date.now() >= this.endAtMs);

    // Manual submit: all questions must be answered. Time-up: save whatever is answered.
    if (!isTimeUp && !this.canSubmit) return;

    if (!isTimeUp) {
      this.showSubmitConfirm = true;
      return;
    }

    this.performSubmit(isTimeUp);
  }

  cancelSubmitConfirm(): void {
    this.showSubmitConfirm = false;
  }

  confirmSubmit(): void {
    this.showSubmitConfirm = false;
    this.performSubmit(false);
  }

  private performSubmit(isTimeUp: boolean): void {
    if (this.completed || this.submitting || this.submitInFlight) return;

    // Lock immediately to avoid duplicate calls from timer ticks / visibility events.
    this.submitInFlight = true;
    this.submitting = true;
    if (isTimeUp) {
      this.stopTimer();
    }

    this.flushTimeForCurrentQuestion();
    const payload = this.buildSavePayload();
    // Debug: verify payload shape in browser console before POST.
    // eslint-disable-next-line no-console
    console.log('[ScenarioAssessment] save payload', payload);
    // eslint-disable-next-line no-console
    console.log('[ScenarioAssessment] attemptStartTime IST', new Date(payload.attemptStartTime).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }));
    // eslint-disable-next-line no-console
    console.log('[ScenarioAssessment] attemptStartTime UTC', new Date(payload.attemptStartTime).toISOString());
    this.api.saveScenarioAssessmentAttempt(payload).subscribe({
      next: () => {
        this.submitting = false;
        this.submitInFlight = false;
        this.stopTimer();

        const timeUpNow =
          this.remainingSeconds <= 0 ||
          (this.endAtMs != null && Date.now() >= this.endAtMs);
        this.completedDueToTimeUp = isTimeUp || timeUpNow;
        this.completed = true;
        this.started = false;
        this.submitted = true;
      },
      error: (err: any) => {
        const status = Number(err?.status ?? 0);
        const apiStatus = String(err?.error?.status ?? err?.error?.data?.status ?? '').toUpperCase();
        const apiMessage = String(err?.error?.message ?? err?.error?.data?.message ?? '').trim();
        const isConflict = status === 409 || apiStatus === 'CONFLICT';

        if (isConflict) {
          // Lock this screen into "already submitted" mode and avoid re-fetching questions on refresh.
          const m = apiMessage || 'Employee has already submitted the test';
          this.alreadySubmitted = true;
          this.alreadySubmittedMessage = m;

          this.started = false;
          this.completed = true;
          this.submitting = false;
          this.submitInFlight = false;
          this.errorMessage = '';
          this.questions = [];
          this.selectedOptionByQuestionId.clear();
          return;
        }

        // Keep UI usable; user can retry submit.
        this.submitting = false;
        this.submitInFlight = false;
        // Only restart timer after a failed manual submit while time remains.
        if (!isTimeUp && this.remainingSeconds > 0 && this.endAtMs && !this.timerId) {
          this.startTimer();
        }
      }
    });
  }

  select(optionId: number): void {
    const q = this.currentQuestion;
    if (!q || this.completed) return;
    this.selectedOptionByQuestionId.set(q.questionId, optionId);
  }

  isSelected(optionId: number): boolean {
    const q = this.currentQuestion;
    if (!q) return false;
    return this.selectedOptionByQuestionId.get(q.questionId) === optionId;
  }

  get currentQuestion(): ScenarioQuestion | null {
    return this.questions[this.currentIndex] ?? null;
  }

  get progressText(): string {
    const cur = Math.min(this.currentIndex + 1, this.questions.length);
    return `${String(cur).padStart(2, '0')}/${String(this.questions.length).padStart(2, '0')}`;
  }

  /** 0..3 (A..D) for the 4-dot domain rail */
  get domainStepIndex(): number {
    const k = this.currentQuestion?.domainKey;
    if (k === 'A') return 0;
    if (k === 'B') return 1;
    if (k === 'C') return 2;
    return 3;
  }

  readonly domainSteps = [0, 1, 2, 3] as const;

  /** Instruction page footer — estimated time from timer config. */
  get estimatedTimeLabel(): string {
    const mins = Math.round(this.totalTimeSeconds / 60);
    return `${mins} mins`;
  }

  /** Instruction page footer — question count from API when loaded. */
  get totalQuestionsLabel(): string {
    const n = this.questions.length;
    return n > 0 ? `${n} Question${n === 1 ? '' : 's'}` : '20 Questions';
  }

  get canGoNext(): boolean {
    if (!this.started || this.completed) return false;
    const q = this.currentQuestion;
    if (!q) return false;
    return !!this.selectedOptionByQuestionId.get(q.questionId);
  }

  get canGoBack(): boolean {
    if (!this.started) return true;
    return this.currentIndex > 0;
  }

  get canSubmit(): boolean {
    if (!this.started || this.completed) return false;
    if (this.questions.length === 0) return false;
    return this.questions.every((q) => !!this.selectedOptionByQuestionId.get(q.questionId));
  }

  get showSubmit(): boolean {
    return this.started && this.currentIndex === this.questions.length - 1 && !this.completed;
  }

  get showNext(): boolean {
    return this.started && this.currentIndex < this.questions.length - 1 && !this.completed;
  }

  get timeText(): string {
    const s = Math.max(0, this.remainingSeconds);
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  }

  /** True when every question has a selected option (full attempt). */
  get allQuestionsWereAnswered(): boolean {
    if (!this.questions.length) return false;
    return this.questions.every((q) => !!this.selectedOptionByQuestionId.get(q.questionId));
  }

  /** Shown only when `completed` — wall-clock session ended and save succeeded (options stay editable until then). */
  get completedLockMessage(): string {
    if (!this.completed) return '';
    if (this.allQuestionsWereAnswered) {
      return 'Assessment saved successfully. The time limit ended; this assessment is locked.';
    }
    return 'Assessment saved successfully. Time ran out before every question was answered; your progress was saved and this assessment is locked.';
  }

  private startTimer(): void {
    this.stopTimer();
    // Use wall-clock deadline so timer stays accurate even when tab is not focused (intervals can be throttled).
    const tick = () => {
      if (!this.started || this.completed) return;
      const end = this.endAtMs;
      if (!end) return;
      const msLeft = Math.max(0, end - Date.now());
      this.remainingSeconds = Math.ceil(msLeft / 1000);
      if (msLeft <= 0 && !this.submitInFlight) {
        this.submit(true);
      }
    };
    tick();
    this.timerId = setInterval(tick, 500);
    document.addEventListener('visibilitychange', this.onVisibilityTick, { passive: true } as any);
  }

  private stopTimer(): void {
    if (this.timerId) clearInterval(this.timerId);
    this.timerId = null;
    // Avoid leaking listeners across repeated starts.
    document.removeEventListener('visibilitychange', this.onVisibilityTick as any);
  }

  // Kept as a stable reference for removeEventListener.
  private readonly onVisibilityTick = () => {
    if (!this.started || this.completed) return;
    const end = this.endAtMs;
    if (!end) return;
    const msLeft = Math.max(0, end - Date.now());
    this.remainingSeconds = Math.ceil(msLeft / 1000);
    if (msLeft <= 0 && !this.submitInFlight) this.submit(true);
  };

  private revokeProfileImageObjectUrl(): void {
    if (this.profileImageRevokeUrl) {
      try {
        URL.revokeObjectURL(this.profileImageRevokeUrl);
      } catch {
        /* ignore */
      }
      this.profileImageRevokeUrl = null;
    }
    this.profileImageObjectUrl = null;
  }

  private loadProfileImage(opts?: { payrollId?: string; tempPayrollId?: string }): void {
    this.revokeProfileImageObjectUrl();
    this.api.getProfileImageBlob(opts).subscribe({
      next: (blob) => {
        if (!blob?.size) return;
        if (blob.type && !blob.type.startsWith('image/')) return;
        const url = URL.createObjectURL(blob);
        this.profileImageRevokeUrl = url;
        this.profileImageObjectUrl = url;
      }
    });
  }

  private loadProfile(): void {
    const payrollId = this.api.getStoredPayrollId();
    const encryptedPayrollId = this.api.getStoredEncryptedPayrollId();
    this.api.getUserProfile(encryptedPayrollId || payrollId).subscribe({
      next: (res: UserProfileResponse) => {
        const dash: any = (res as any)?.employeeDashBoardInfo ?? {};
        const basic: any = (res as any)?.basicInfo ?? {};

        const name = String(dash?.empName ?? '').trim();
        const campus = String(dash?.campusName ?? '').trim();
        const profilePayroll = String(dash?.payrollId ?? '').trim();
        const profileTemp = String(dash?.tempPayrollId ?? '').trim();
        const pid = profilePayroll || profileTemp || payrollId;

        const designation = String(basic?.designation ?? '').trim();

        if (name) this.staffName = name;
        else this.staffName = '—';

        this.staffPayrollId = pid || '—';

        const des =
          designation && designation !== '-' ? designation : '';
        this.staffDesignation = des || '—';

        this.staffCampus = campus || '—';

        this.loadProfileImage({
          payrollId: profilePayroll || profileTemp || undefined,
          tempPayrollId: profileTemp || undefined
        });
      },
      error: () => {
        this.loadProfileImage();
      }
    });
  }

  private loadQuestions(): void {
    this.loading = true;
    this.errorMessage = '';
    this.api.getScenarioAssessmentQuestionAndOptionDetails().subscribe({
      next: (sections: ScenarioAssessmentSectionDto[]) => {
        this.sectionsRaw = Array.isArray(sections) ? sections : [];
        const mapped = this.mapSectionsToQuestions(this.sectionsRaw);
        this.questions = mapped;
        this.loading = false;
        if (!this.questions.length) {
          this.errorMessage = 'No questions found.';
        }
      },
      error: (err: any) => {
        const status = Number(err?.status ?? 0);
        const apiStatus = String(err?.error?.status ?? err?.error?.data?.status ?? '').toUpperCase();
        const apiMessage = String(err?.error?.message ?? err?.error?.data?.message ?? '').trim();
        const isConflict = status === 409 || apiStatus === 'CONFLICT';

        if (isConflict) {
          const m = apiMessage || 'Employee has already submitted the test';
          this.alreadySubmitted = true;
          this.alreadySubmittedMessage = m;

          // Hide all other content.
          this.started = false;
          this.completed = true;
          this.loading = false;
          this.errorMessage = '';
          this.questions = [];
          this.selectedOptionByQuestionId.clear();
          this.stopTimer();
          return;
        }

        this.loading = false;
        this.questions = [];
        this.errorMessage = 'Could not load scenario questions. Please try again.';
      }
    });
  }

  private mapSectionsToQuestions(sections: ScenarioAssessmentSectionDto[]): ScenarioQuestion[] {
    const list = Array.isArray(sections) ? sections : [];
    const bySection = [...list].sort((a, b) => Number(a.sectionId ?? 0) - Number(b.sectionId ?? 0));
    const out: ScenarioQuestion[] = [];
    let no = 1;

    for (const sec of bySection) {
      const examId = Number(sec?.examId ?? 0);
      const sectionId = Number(sec?.sectionId ?? 0);
      const domainKey = sectionIdToDomainKey(sectionId);
      const domainTitle = String(sec?.sectionName ?? '').trim() || 'Section';
      const qList = Array.isArray(sec?.questionDetailsList) ? sec.questionDetailsList : [];

      for (const q of qList) {
        const questionId = Number(q?.questionId ?? 0);
        const title = String(q?.questionTitle ?? '').trim();
        const prompt = String(q?.questionText ?? '').trim() || 'What would you prioritise?';
        const situation = String(q?.questionScenario ?? '').trim();
        const optionsRaw = Array.isArray(q?.optionsList) ? q.optionsList : [];
        const options = [...optionsRaw]
          .map((o) => ({
            id: Number(o?.optionId ?? 0),
            text: String(o?.optionText ?? '').trim(),
            sequence: String(o?.optionSequence ?? '').trim()
          }))
          .filter((o) => Number.isFinite(o.id) && o.id > 0 && !!o.text)
          .sort((a, b) => String(a.sequence).localeCompare(String(b.sequence)) || a.id - b.id);

        if (!options.length) continue;
        if (!Number.isFinite(questionId) || questionId <= 0) continue;
        out.push({
          no,
          domainKey,
          domainTitle,
          examId,
          sectionId,
          questionId,
          title,
          prompt,
          situation,
          options
        });
        no += 1;
      }
    }
    return out;
  }

  private flushTimeForCurrentQuestion(): void {
    if (!this.started || this.completed) return;
    const q = this.currentQuestion;
    if (!q) return;
    const shownAt = this.questionShownAtMs;
    if (!shownAt) {
      this.questionShownAtMs = Date.now();
      return;
    }
    const deltaSec = Math.max(0, Math.round((Date.now() - shownAt) / 1000));
    if (deltaSec <= 0) return;
    const prev = this.timeTakenByQuestionId.get(q.questionId) ?? 0;
    this.timeTakenByQuestionId.set(q.questionId, prev + deltaSec);
  }

  private buildSavePayload(): ScenarioAssessmentSavePayload {
    const attemptStartTime = Number(this.attemptStartAtMs ?? Date.now());
    const attemptendTIme = Number(Date.now());
    const examId = Number(this.sectionsRaw?.[0]?.examId ?? this.questions[0]?.examId ?? 0) || 0;

    const sectionsMap = new Map<number, { sectionId: number; attemptedQuestions: Array<{ questionId: number; optionId: number; timeTaken: number }> }>();
    for (const q of this.questions) {
      const optionId = this.selectedOptionByQuestionId.get(q.questionId);
      if (!optionId) continue;
      const timeTaken = Number(this.timeTakenByQuestionId.get(q.questionId) ?? 0) || 0;
      if (!sectionsMap.has(q.sectionId)) {
        sectionsMap.set(q.sectionId, { sectionId: q.sectionId, attemptedQuestions: [] });
      }
      sectionsMap.get(q.sectionId)!.attemptedQuestions.push({
        questionId: q.questionId,
        optionId,
        timeTaken
      });
    }

    const sections = [...sectionsMap.values()].sort((a, b) => a.sectionId - b.sectionId);
    const attemptedCount = sections.reduce((sum, s) => sum + s.attemptedQuestions.length, 0);

    return {
      examId,
      attemptStartTime,
      attemptendTIme,
      sections,
      attemptedCount
    };
  }
}

function sectionIdToDomainKey(sectionId: number): DomainKey {
  switch (Number(sectionId)) {
    case 1:
      return 'A';
    case 2:
      return 'B';
    case 3:
      return 'C';
    default:
      return 'D';
  }
}


